import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calculator, Grid3x3, FileText, ShieldAlert, Target } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Typewriter } from "@/components/Typewriter";
import { ArticleCard } from "@/components/ArticleCard";
import { AdSlot } from "@/components/AdSlot";

const HERO_IMG =
  "https://images.unsplash.com/photo-1603378596501-8bbfc7059d5e?w=1600&q=80";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/articles", { params: { featured: true, limit: 6 } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  useSeo({
    title: "Licence to Play — Baccarat Rules, Strategy & Free Tools",
    description:
      "Your mission-briefing guide to baccarat. Free rules, odds, strategy dossiers, a staking calculator and an interactive shoe pattern board.",
    keywords: "baccarat, baccarat rules, baccarat strategy, baccarat odds, how to play baccarat",
    image: HERO_IMG,
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Licence to Play",
      description: "Free baccarat education, tools and strategy.",
    },
  });

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grain-overlay border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-28 z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="overline text-gold mb-6"
          >
            ▸ Classified Briefing // Baccarat Division
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-white tracking-tighter max-w-4xl leading-[1.02]">
            <Typewriter text="Master the game of chance." speed={45} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.5 }}
            className="mt-7 text-lg text-white/65 max-w-2xl leading-relaxed"
          >
            The definitive field guide to baccarat — rules, odds and strategy decoded, backed by two
            interactive tools built like a Q Branch dashboard. Free, forever.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.5 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/system"
              data-testid="hero-system-cta"
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-gold text-black font-semibold text-sm border border-gold hover:bg-transparent hover:text-gold transition-colors"
            >
              Read The System <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/q-branch"
              data-testid="hero-qbranch-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/25 text-white font-semibold text-sm hover:border-gold hover:text-gold transition-colors"
            >
              <Calculator className="w-4 h-4" /> Open Q Branch
            </Link>
          </motion.div>

          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-3xl">
            {[
              ["1.06%", "Banker house edge"],
              ["24+", "Strategy dossiers"],
              ["2", "Free live tools"],
              ["0", "Fees. Ever."],
            ].map(([n, l]) => (
              <div key={l} className="bg-background px-5 py-5">
                <div className="font-mono text-2xl text-gold">{n}</div>
                <div className="overline text-white/40 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-8">
        <AdSlot size="728x90" label="Header Leaderboard" />
      </div>

      {/* TOOLS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline text-gold mb-2">▸ Field Kit</p>
            <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight">Q Branch Equipment</h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <ToolCard
            to="/q-branch"
            testid="tool-qbranch"
            icon={<Target className="w-6 h-6" />}
            tag="Calculator"
            title="Staking Calculator"
            desc="Feed in your bankroll and receive a full negative-progression staking ladder with stop-loss and stop-win targets — plus honest risk warnings."
          />
          <ToolCard
            to="/shoe-board"
            testid="tool-shoeboard"
            icon={<Grid3x3 className="w-6 h-6" />}
            tag="Tracker"
            title="Shoe Pattern Board"
            desc="Log Banker, Player and Tie outcomes on an interactive bead plate and big road. Your session persists between visits."
          />
        </div>
      </section>

      {/* FEATURED ARTICLES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline text-gold mb-2">▸ Intelligence</p>
            <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight">Featured Dossiers</h2>
          </div>
          <Link to="/dossier" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-gold transition-colors">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {featured.length === 0 ? (
          <p className="font-mono text-sm text-white/40">Decrypting briefings…</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((a, i) => (
              <ArticleCard key={a.slug} article={a} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* SYSTEM TEASER */}
      <section className="border-y border-white/10 bg-[#0a0c0c] relative grain-overlay">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div>
            <p className="overline text-gold mb-4">▸ Operation: The System</p>
            <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight leading-tight">
              The New Baccarat System, fully declassified.
            </h2>
            <p className="mt-5 text-white/60 leading-relaxed">
              Back the Player every hand and follow a disciplined staking plan. We break down exactly how it
              works, where the risks hide, and hand you a free PDF briefing.
            </p>
            <Link
              to="/system"
              data-testid="system-teaser-cta"
              className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 border border-gold text-gold font-semibold text-sm hover:bg-gold hover:text-black transition-colors"
            >
              <FileText className="w-4 h-4" /> Get the free briefing
            </Link>
          </div>
          <div className="border border-white/10 bg-background p-8">
            <div className="flex items-center gap-3 text-crimson-bright mb-4">
              <ShieldAlert className="w-5 h-5" />
              <span className="overline">Reality Check</span>
            </div>
            <p className="text-white/70 leading-relaxed text-sm">
              No staking system can overcome baccarat's built-in house edge. The System is a discipline and
              bankroll framework — not a guaranteed path to profit. We tell you the maths straight.
            </p>
            <Link to="/dossier/baccarat-odds-and-house-edge" className="mt-5 inline-block text-sm text-gold hover:underline">
              See the odds for yourself →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const ToolCard = ({ to, testid, icon, tag, title, desc }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group relative border border-white/10 bg-card p-7 hover:border-gold/40 transition-colors overflow-hidden"
  >
    <div className="flex items-center justify-between mb-6">
      <div className="w-12 h-12 grid place-items-center border border-gold/30 text-gold group-hover:bg-gold group-hover:text-black transition-colors">
        {icon}
      </div>
      <span className="overline text-white/30">{tag}</span>
    </div>
    <h3 className="font-display text-2xl text-white tracking-tight group-hover:text-gold transition-colors">{title}</h3>
    <p className="mt-3 text-sm text-white/55 leading-relaxed">{desc}</p>
    <div className="mt-6 inline-flex items-center gap-2 text-sm text-gold">
      Launch tool <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
    </div>
  </Link>
);
